import { ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import ConfirmDeleteModal from '@/shared/ui/modals/ConfirmDeleteModal.vue';
import CategoryIcon from '@/shared/assets/icons/CategoryIcon.svg';
import type { SearchComboboxOption } from '@/shared/ui/search-combobox';
import { isCategoryNameConflictError, matrixCategoryApi } from '../api/form-builder.api';
import { useFormBuilderToast } from './useFormBuilderToast';

export const CREATE_CATEGORY_CONFIRMATION_TITLE = 'Deseja cadastrar uma nova categoria?';
export const CREATE_CATEGORY_CONFIRMATION_DESCRIPTION =
  'Ao cadastrar uma nova categoria, ela pode ser utilizada em outros Modelos.';
export const CREATE_CATEGORY_ERROR_MESSAGE = 'Erro ao cadastrar categoria, tente novamente.';

function normalizeCategoryName(name: string): string {
  return name.trim().toLocaleLowerCase('pt-BR').normalize('NFD').replace(/\p{M}/gu, '');
}

function toOption(category: { id: number; name: string }): SearchComboboxOption {
  return { value: String(category.id), label: category.name };
}

export function useMatrixCategoryCreation(
  categories: Ref<SearchComboboxOption[]>,
  onResolved: (option: SearchComboboxOption) => void
) {
  const { showErrorToast } = useFormBuilderToast();
  const isCreatingCategory = ref(false);

  function findCategoryByName(name: string): SearchComboboxOption | undefined {
    const normalized = normalizeCategoryName(name);
    return categories.value.find((option) => normalizeCategoryName(option.label) === normalized);
  }

  async function resolveConflictFromServer(name: string): Promise<SearchComboboxOption | null> {
    try {
      categories.value = (await matrixCategoryApi.list()).map(toOption);
    } catch (error) {
      console.error('Error reloading matrix categories:', error);
    }
    return findCategoryByName(name) ?? null;
  }

  async function createCategory(name: string): Promise<SearchComboboxOption | null> {
    try {
      const option = toOption(await matrixCategoryApi.create(name));
      categories.value = [...categories.value, option];
      return option;
    } catch (error) {
      const existing = isCategoryNameConflictError(error)
        ? await resolveConflictFromServer(name)
        : null;
      if (existing) return existing;

      console.error('Error creating matrix category:', error);
      showErrorToast({ title: CREATE_CATEGORY_ERROR_MESSAGE, type: 'error', hideIcon: true });
      return null;
    }
  }

  function requestCategory(name: string): void {
    const trimmed = name.trim();
    if (!trimmed || isCreatingCategory.value) return;

    const existing = findCategoryByName(trimmed);
    if (existing) {
      onResolved(existing);
      return;
    }

    const { open, close } = useModal({
      component: ConfirmDeleteModal,
      attrs: {
        icon: CategoryIcon,
        iconAlt: 'Cadastrar categoria',
        title: CREATE_CATEGORY_CONFIRMATION_TITLE,
        description: CREATE_CATEGORY_CONFIRMATION_DESCRIPTION,
        get isDeleting() {
          return isCreatingCategory.value;
        },
        onClose() {
          close();
        },
        async onConfirm() {
          isCreatingCategory.value = true;
          const option = await createCategory(trimmed);
          isCreatingCategory.value = false;
          close();
          if (option) onResolved(option);
        },
      },
    });

    open();
  }

  return {
    isCreatingCategory,
    requestCategory,
  };
}
